import React from 'react';
// import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Stack,
  Fade,
  Slide,
  Zoom
} from '@mui/material';
import {
  LocalHospital,
  Psychology,
  MedicalServices,
  HealthAndSafety,
  Business,
  AccountBalance
} from '@mui/icons-material';
import Footer from '../shared/Footer';
import ContactForm from '../shared/ContactForm';
import CommonHeader from '../shared/CommonHeader';
import styles from '../landing/LandingPage.module.scss';

const PlatformBuilder = () => {
  // const navigate = useNavigate();

  // Prevent auto-scroll on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFormSuccess = (response) => {
    console.log('Form submitted successfully:', response);
    // You can add additional success handling here if needed
  };

  const handleFormError = (error) => {
    console.error('Form submission failed:', error);
    // You can add additional error handling here if needed
  };

  const useCases = [
    {
      icon: <Psychology sx={{ fontSize: 48, color: '#2259c9' }} />,
      title: 'Behavioral Health Providers',
      description: 'Transition tracking across inpatient/outpatient'
    },
    {
      icon: <Business sx={{ fontSize: 48, color: '#2259c9' }} />,
      title: 'Reentry & Housing Programs',
      description: 'Customized milestones, outcome metrics, alerts'
    },
    {
      icon: <MedicalServices sx={{ fontSize: 48, color: '#2259c9' }} />,
      title: 'Pediatric Service Orgs',
      description: 'Cross-provider coordination, reports, family view'
    },
    {
      icon: <HealthAndSafety sx={{ fontSize: 48, color: '#2259c9' }} />,
      title: 'Palliative/Hospice Care',
      description: 'Patient journey insights, alerts, comfort outcomes'
    },
    {
      icon: <LocalHospital sx={{ fontSize: 48, color: '#2259c9' }} />,
      title: 'Addiction Treatment Centers',
      description: 'Customized workflows, relapse tracking, reports'
    }
  ];

  return (
    <div className={styles.landingPage}>
      {/* Header */}
      <CommonHeader showLoginButton={false} />

      {/* Hero Section - Card-based Layout */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 6 }}>
        <Grid container spacing={6} alignItems="center" sx={{ minHeight: '80vh' }}>
          {/* Left Content */}
          <Grid item xs={12} lg={6}>
            <Fade in={true} timeout={1000}>
              <Box>
                <Slide direction="right" in={true} timeout={1200}>
                  <Typography 
                    variant="h2" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 700,
                      color: '#2259c9',
                      mb: 3,
                      lineHeight: 1.2
                    }}
                  >
                    Build Your Platform
                  </Typography>
                </Slide>

                <Slide direction="right" in={true} timeout={1400}>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    sx={{ 
                      color: '#495057',
                      mb: 4,
                      fontWeight: 500,
                      lineHeight: 1.4
                    }}
                  >
                    Built for skilled nursing. Ready for any care-driven business.
                  </Typography>
                </Slide>

                <Fade in={true} timeout={1600}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.1rem',
                      lineHeight: 1.7,
                      color: '#6c757d',
                      mb: 4
                    }}
                  >
                    Whether you call them patients, clients, or residents—if you track transitions, we can help.
                    Our platform adapts to fit your workflows, labels, and outcomes—so you're not bending your operations to fit someone else's dashboard.
                  </Typography>
                </Fade>

                <Zoom in={true} timeout={1800}>
                  <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                    <Button
                      variant="contained"
                      size="large"
                      sx={{ 
                        px: 4, 
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        backgroundColor: '#2259c9',
                        '&:hover': {
                          backgroundColor: '#1a4a9e'
                        }
                      }}
                      onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                      Get Started
                    </Button>                   
                  </Stack>
                </Zoom>
              </Box>
            </Fade>
          </Grid>

          {/* Right Visual */}
          <Grid item xs={12} lg={6}>
            <Fade in={true} timeout={2000}>
              <Box sx={{ position: 'relative' }}>
                {/* Main Feature Card */}
                <Zoom in={true} timeout={2200}>
                  <Box
                    sx={{
                      backgroundColor: '#ffffff',
                      borderRadius: 4,
                      boxShadow: '0 20px 40px rgba(34, 89, 201, 0.1)',
                      p: 4,
                      position: 'relative',
                      transform: 'rotate(-2deg)',
                      mb: 3
                    }}
                  >
                    <Typography variant="h6" sx={{ color: '#2259c9', mb: 2, fontWeight: 600 }}>
                      ✨ Customizable Platform
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6c757d', lineHeight: 1.6 }}>
                      Adapt workflows, labels, and outcomes to match your organization's unique needs
                    </Typography>
                  </Box>
                </Zoom>

                {/* Secondary Cards */}
                <Slide direction="left" in={true} timeout={2400}>
                  <Box
                    sx={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: 3,
                      p: 3,
                      ml: 4,
                      transform: 'rotate(1deg)',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.05)'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ color: '#495057', mb: 1, fontWeight: 600 }}>
                      🎯 Multi-Industry Ready
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6c757d' }}>
                      From behavioral health to addiction treatment centers
                    </Typography>
                  </Box>
                </Slide>

                <Slide direction="right" in={true} timeout={2600}>
                  <Box
                    sx={{
                      backgroundColor: '#e9ecef',
                      borderRadius: 3,
                      p: 3,
                      mr: 4,
                      mt: 2,
                      transform: 'rotate(-1deg)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.03)'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ color: '#495057', mb: 1, fontWeight: 600 }}>
                      🚀 Quick Implementation
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6c757d' }}>
                      Start tracking transitions in days, not months
                    </Typography>
                  </Box>
                </Slide>

                {/* Floating Elements */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 60,
                    height: 60,
                    backgroundColor: '#2259c9',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'float 3s ease-in-out infinite',
                    '@keyframes float': {
                      '0%, 100%': { transform: 'translateY(0px)' },
                      '50%': { transform: 'translateY(-10px)' }
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'white' }}>💡</Typography>
                </Box>

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -10,
                    left: -10,
                    width: 40,
                    height: 40,
                    backgroundColor: '#28a745',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'float 3s ease-in-out infinite 1s',
                  }}
                >
                  <Typography variant="body1" sx={{ color: 'white' }}>✓</Typography>
                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* Use Cases Section */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ py: 8 }}>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography variant="h2" component="h2" className={styles.sectionTitle} sx={{ mb: 2 }}>
              <AccountBalance sx={{ fontSize: 40, mr: 2, verticalAlign: 'middle', color: '#2259c9' }} />
              Who Else Can Use Our Model
            </Typography>
            <Typography variant="h6" className={styles.sectionSubtitle}>
              Examples of organizations we can customize for
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {useCases.map((useCase, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Zoom in={true} timeout={1000 + index * 200}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      minHeight: 180,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Box sx={{ mb: 2 }}>
                        {useCase.icon}
                      </Box>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {useCase.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d' }}>
                        {useCase.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Closing Statement */}
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Fade in={true} timeout={2000}>
            <Box>
              <Typography variant="h3" component="h2" className={styles.sectionTitle} sx={{ mb: 3 }}>
                <Psychology sx={{ fontSize: 40, mr: 2, verticalAlign: 'middle', color: '#2259c9' }} />
                We Don't Just Offer Dashboards
              </Typography>
              <Typography variant="h6" className={styles.sectionSubtitle} sx={{ mb: 4 }}>
                We build a system that mirrors your reality—however you define it.
              </Typography>
            </Box>
          </Fade>
        </Box>

        {/* Contact Form Section */}
        <Box id="contact-form">
          <ContactForm 
            onSuccess={handleFormSuccess}
            onError={handleFormError}
          />
        </Box>
      </Container>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PlatformBuilder; 